import type { QuoteDTO, UpdateQuoteDTO } from '../types/quotes'
import { calendarDateInTimeZone, isCalendarDate } from './calendar-date'

function instant(value: string | null | undefined): Date | null {
  if (
    !value ||
    !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value,
    ) ||
    !isCalendarDate(value.slice(0, 10))
  )
    return null
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

function namedZone(value: string | null | undefined): string | null {
  if (!value || /^[+-]/.test(value)) return null
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: value,
    }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

export interface QuoteExpiryDraft {
  day: string
  originalDay: string
  original: string | null
  timezone: string | null
}

/** Freeze the displayed business zone with the draft; refreshes must not reinterpret edits. */
export function createQuoteExpiryDraft(
  quote?: Pick<QuoteDTO, 'validUntil' | 'organizationTimezone'>,
): QuoteExpiryDraft {
  const timezone = namedZone(quote?.organizationTimezone)
  const original = quote?.validUntil ?? null
  const date = instant(original)
  const day = date ? calendarDateInTimeZone(date, timezone ?? 'UTC') : ''
  return { day, originalDay: day, original, timezone }
}

/** An unrelated edit preserves the exact existing instant, including legacy precision. */
export function quoteExpiryUpdate(
  draft: QuoteExpiryDraft,
): Pick<UpdateQuoteDTO, 'validUntilDate'> {
  if (draft.day === draft.originalDay) return {}
  if (!draft.day) return { validUntilDate: null }
  if (!isCalendarDate(draft.day)) throw new Error('Enter a valid expiry date.')
  if (!draft.timezone)
    throw new Error(
      'Workspace timezone is unavailable. Reload the quote before changing its expiry.',
    )
  return { validUntilDate: { day: draft.day, timezone: draft.timezone } }
}

/** Exact cutoff in its saved business zone; legacy instants explicitly display UTC. */
export function formatQuoteExpiry(
  value: string | null | undefined,
  timezone?: string | null,
  locales?: string | string[],
): string {
  const date = instant(value)
  if (!date) return 'Expiry unavailable'
  return new Intl.DateTimeFormat(locales, {
    timeZone: namedZone(timezone) ?? 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

/** The cutoff's calendar day in its saved zone, for tables ("Jul 20, 2026"). */
export function formatQuoteExpiryDay(
  value: string | null | undefined,
  timezone?: string | null,
  locales?: string | string[],
): string {
  const date = instant(value)
  if (!date) return 'Expiry unavailable'
  return new Intl.DateTimeFormat(locales, {
    timeZone: namedZone(timezone) ?? 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}
